import { useTheme } from "@emotion/react";
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import FormService from "../../services/doctorServices/FormService";
import FormulaService from "../../services/doctorServices/FormulaService";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Grid,
} from "@mui/material";
import Headeer from "../../components/Headeer";
import TableChartIcon from "@mui/icons-material/TableChart";
import ImageIcon from "@mui/icons-material/Image";
import CartilageMap from "../../components/CartilageMap";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import StarOutlineOutlinedIcon from "@mui/icons-material/StarOutlineOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import RatingScaleSlider from "../../components/RatingScaleSlider";

export default function FilledFormInfo() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('forms');
  const formService = new FormService();
  const formulaService = new FormulaService();
  const { formId } = useParams();
  const [formInfo, setFormInfo] = useState(null);
  /** API bazen nested ScoringFormula dönmez; scoring_formula_id ile ayrıca yüklenir */
  const [scoringFormulaFromApi, setScoringFormulaFromApi] = useState(null);
  const [showOnlySelected, setShowOnlySelected] = useState({}); // Her soru için ayrı state
  const [exportingExcel, setExportingExcel] = useState(false);

  const questionMeta = useMemo(() => ({
    text: {
      title: t('freeTextQuestion'),
      subtitle: t('patientProvidedWrittenAnswer'),
      gradient: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[400]} 100%)`,
      icon: <TextFieldsOutlinedIcon />,
      chipLabel: t('text'),
    },
    multiple_choice: {
      title: t('multipleChoiceQuestion'),
      subtitle: t('singleOptionSelectedByPatient'),
      gradient: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[500]} 100%)`,
      icon: <FormatListBulletedOutlinedIcon />,
      chipLabel: t('multipleChoice'),
    },
    table: {
      title: t('tableQuestion'),
      subtitle: t('scoresCollectedAcrossMatrixRows'),
      gradient: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
      icon: <TableChartIcon />,
      chipLabel: t('table'),
    },
    image: {
      title: t('imageQuestionICRS'),
      subtitle: t('anatomicalRegionsMarkedByPatient'),
      gradient: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
      icon: <ImageIcon />,
      chipLabel: t('image'),
    },
    rating_scale: {
      title: t('ratingScaleQuestion'),
      subtitle: t('ratingScaleSubtitle'),
      gradient: `linear-gradient(135deg, ${colors.redAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
      icon: <LinearScaleIcon />,
      chipLabel: "VAS",
    },
    default: {
      title: t('question'),
      subtitle: "",
      gradient: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`,
      icon: <DescriptionOutlinedIcon />,
      chipLabel: t('question'),
    },
  }), [t, colors]);

  useEffect(() => {
    const fetchFormInfo = async () => {
      try {
        setScoringFormulaFromApi(null);
        const response = await formService.getAnsweredFormInfo(formId);
        setFormInfo(response);
        console.log("Form bilgileri başarıyla getirildi: ", response);
      } catch (error) {
        console.log("Form bilgileri getirilirken bir sorun oluştu: ", error);
      }
    };
    fetchFormInfo();
  }, [formId]);

  useEffect(() => {
    if (!formInfo) return;
    const nested =
      formInfo.ScoringFormula || formInfo.Form?.ScoringFormula;
    if (nested) {
      setScoringFormulaFromApi(null);
      return;
    }
    const formulaId =
      formInfo.scoring_formula_id || formInfo.Form?.scoring_formula_id;
    if (!formulaId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await formulaService.getFormula(formulaId);
        if (!cancelled && data) setScoringFormulaFromApi(data);
      } catch (e) {
        console.warn("Puanlama formülü ayrıca yüklenemedi:", e?.message || e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formInfo]); 

  if (!formInfo) return <div>{t('loading')}</div>;

  const handleExportExcel = async () => {
    try {
      setExportingExcel(true);
      const response = await formService.exportAnsweredFormExcel(formId);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const contentDisposition = response.headers?.["content-disposition"] || "";
      const match = contentDisposition.match(/filename="?([^"]+)"?/i);
      const fileName = match?.[1] || `form-answer-${formId}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Form answer excel export failed:", error);
    } finally {
      setExportingExcel(false);
    }
  };

  const questions = formInfo.questions;
  const formQuestions = formInfo.Form?.questions || [];

  const formName = formInfo?.name || formInfo?.Form?.name || "—";
  const formDescription =
    formInfo?.description || formInfo?.Form?.description || "—";
  const formTypeRaw = formInfo?.Form?.type || formInfo?.type || "—";
  const formTypeLabel =
    formTypeRaw === "for me"
      ? t('forDoctor')
      : formTypeRaw === "for patient"
      ? t('forPatient')
      : formTypeRaw || "—";
  const patient =
    formInfo?.Patient ||
    formInfo?.Form?.Patient ||
    formInfo?.patient ||
    formInfo?.patientInfo ||
    {};
  const patientFullName =
    patient && (patient.firstName || patient.lastName)
      ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim()
      : "—";
  const patientEmail =
    patient?.email || formInfo?.patientEmail || formInfo?.email || "—";
  const fileName =
    formInfo?.File?.name ||
    formInfo?.Form?.File?.name ||
    formInfo?.formFile?.name ||
    "—";
  const submissionDate = formInfo?.createdAt
    ? new Date(formInfo.createdAt).toLocaleString()
    : "—";
  const totalScore =
    formInfo?.point !== undefined && formInfo?.point !== null
      ? Number(formInfo.point).toFixed(1)
      : null;
  /** Cevapta kullanılan formül öncelikli; yoksa form şablonundaki; yoksa ID ile çekilen */
  const scoringFormula =
    formInfo?.ScoringFormula ||
    formInfo?.Form?.ScoringFormula ||
    scoringFormulaFromApi ||
    null;
  const scoringFormulaName = scoringFormula?.name || "—";
  const scoringFormulaExpression =
    scoringFormula?.formula_expression != null &&
    String(scoringFormula.formula_expression).trim() !== ""
      ? String(scoringFormula.formula_expression).trim()
      : "—";
  const scoringFormulaDescription =
    scoringFormula?.description != null &&
    String(scoringFormula.description).trim() !== ""
      ? String(scoringFormula.description).trim()
      : "";
  const formNameLength = formName === "—" ? 0 : formName.length;
  const formDescriptionLength =
    formDescription === "—" ? 0 : formDescription.length;
  return (
    <Box sx={{ marginLeft: 2, marginRight: 2 }}>
      <Headeer title={t('formInformation')} subtitle={t('filledFormInformationPage')} />
      <Box sx={{ width: "90%", ml: 7, mt: 1, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportExcel}
          disabled={exportingExcel}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2.5,
            px: 2.5,
            py: 1,
          }}
        >
          {exportingExcel ? "Exporting..." : "Export Form Answer Excel"}
        </Button>
      </Box>

      <Box sx={{ width: "90%", ml: 7, mt: 5 }}>
        <Box
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: `0 16px 36px ${colors.primary[900]}50`,
            border: `1px solid ${colors.primary[200]}30`,
            backgroundColor: colors.primary[500],
            mb: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2,
              px: { xs: 2.5, md: 3.5 },
              py: 2.5,
              background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: 3,
                  backgroundColor: colors.primary[500],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: colors.grey[100],
                  boxShadow: `0 10px 26px ${colors.primary[900]}55`,
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 30 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: colors.grey[100],
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    fontSize: "1.1rem",
                  }}
                >
                  {t('submissionOverview')}
                </Typography>
                <Typography sx={{ color: colors.grey[200], opacity: 0.85 }}>
                  {t('reviewSubmittedFormDetails')}
                </Typography>
              </Box>
            </Box>

            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                rowGap: 1.5,
              }}
            >
              <Chip
                icon={<PersonOutlineIcon sx={{ fontSize: 18 }} />}
                label={patientFullName}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1.2,
                }}
              />
              <Chip
                icon={<EmailOutlinedIcon sx={{ fontSize: 18 }} />}
                label={patientEmail}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1.2,
                }}
              />
              <Chip
                icon={<FolderOpenOutlinedIcon sx={{ fontSize: 18 }} />}
                label={fileName}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1.2,
                }}
              />
              <Chip
                icon={<AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />}
                label={submissionDate}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1.2,
                }}
              />
              <Chip
                label={formTypeLabel}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1.2,
                }}
              />
              {totalScore !== null && (
                <Chip
                  icon={<StarOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
                  label={t('scoreLabel', { score: totalScore })}
                  sx={{
                    backgroundColor: `${colors.greenAccent[600]}aa`,
                    color: colors.grey[100],
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 1.2,
                  }}
                />
              )}
            </Stack>
          </Box>

          <Box
            sx={{
              px: { xs: 2.5, md: 3.5 },
              py: 3,
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: colors.grey[300],
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {t('formTitle')}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formName}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: 3,
                      backgroundColor: colors.primary[500],
                      fontSize: "1.6rem",
                      fontWeight: 700,
                      color: colors.grey[100],
                      "& fieldset": {
                        borderColor: `${colors.primary[200]}44`,
                      },
                      "&:hover fieldset": {
                        borderColor: colors.greenAccent[400],
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.greenAccent[500],
                        boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                      },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "right",
                    mt: 0.5,
                    color: colors.grey[400],
                  }}
                >
                  {t('charactersCount', { count: formNameLength })}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: colors.grey[300],
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {t('formDescription')}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={3}
                  value={formDescription}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: 3,
                      backgroundColor: colors.primary[500],
                      color: colors.grey[200],
                      "& fieldset": {
                        borderColor: `${colors.primary[200]}33`,
                      },
                      "&:hover fieldset": {
                        borderColor: colors.blueAccent[300],
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.blueAccent[200],
                        boxShadow: `0 0 0 3px ${colors.blueAccent[400]}33`,
                      },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "right",
                    mt: 0.5,
                    color: colors.grey[400],
                  }}
                >
                  {t('charactersCount', { count: formDescriptionLength })}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: colors.grey[300],
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {t('patientName')}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={patientFullName}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: 3,
                      backgroundColor: colors.primary[500],
                      color: colors.grey[100],
                      "& fieldset": {
                        borderColor: `${colors.primary[200]}33`,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: colors.grey[300],
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {t('patientEmail')}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={patientEmail}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: 3,
                      backgroundColor: colors.primary[500],
                      color: colors.grey[100],
                      "& fieldset": {
                        borderColor: `${colors.primary[200]}33`,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: colors.grey[300],
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {t('fileName')}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={fileName}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: 3,
                      backgroundColor: colors.primary[500],
                      color: colors.grey[100],
                      "& fieldset": {
                        borderColor: `${colors.primary[200]}33`,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: colors.grey[300],
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {t('formType')}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formTypeLabel}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: 3,
                      backgroundColor: colors.primary[500],
                      color: colors.grey[100],
                      "& fieldset": {
                        borderColor: `${colors.primary[200]}33`,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: colors.grey[300],
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {t('submissionDate')}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={submissionDate}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: 3,
                      backgroundColor: colors.primary[500],
                      color: colors.grey[100],
                      "& fieldset": {
                        borderColor: `${colors.primary[200]}33`,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: colors.grey[300],
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {t("scoringFormula")}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={scoringFormulaName}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: 3,
                      backgroundColor: colors.primary[500],
                      color: colors.grey[100],
                      "& fieldset": {
                        borderColor: `${colors.primary[200]}33`,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: colors.grey[300],
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {t("scoringFormulaExpression")}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={scoringFormulaExpression === "—" ? 1 : 2}
                  value={scoringFormulaExpression}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: 3,
                      backgroundColor: colors.primary[600],
                      color: colors.greenAccent[400],
                      fontFamily: "monospace",
                      fontSize: "0.95rem",
                      "& fieldset": {
                        borderColor: `${colors.greenAccent[600]}44`,
                      },
                    },
                  }}
                />
              </Grid>

              {scoringFormulaDescription ? (
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      color: colors.grey[300],
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    {t("scoringFormulaDescriptionLabel")}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    multiline
                    minRows={2}
                    value={scoringFormulaDescription}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        borderRadius: 3,
                        backgroundColor: colors.primary[500],
                        color: colors.grey[200],
                        "& fieldset": {
                          borderColor: `${colors.primary[200]}33`,
                        },
                      },
                    }}
                  />
                </Grid>
              ) : null}

              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: colors.grey[300],
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {t('totalImportanceScore')}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={totalScore !== null ? totalScore : "—"}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: 3,
                      backgroundColor: colors.primary[500],
                      fontSize: "1.4rem",
                      fontWeight: 700,
                      color: colors.greenAccent[400],
                      "& fieldset": {
                        borderColor: `${colors.greenAccent[400]}55`,
                      },
                      "&:hover fieldset": {
                        borderColor: colors.greenAccent[400],
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.greenAccent[500],
                        boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box>
          {questions.map((field, index) => {
            const formQuestion = formQuestions[index] || {};
            const meta = questionMeta[field.type] || questionMeta.default;

            const textAnswer =
              field.type === "text"
                ? typeof field.answer === "string"
                  ? field.answer
                  : field.answer ?? ""
                : "";

            let tableAnswers = {};
            if (field.type === "table") {
              try {
                const parsed =
                  typeof field.answer === "string"
                    ? JSON.parse(field.answer)
                    : field.answer || {};
                
                // Yeni format: {"0_0": "Header1", "0_1": "Header2"} veya eski format: {"0": "Header1", "1": "Header2"}
                // Her iki formatı da destekle
                tableAnswers = parsed;
              } catch (error) {
                tableAnswers = {};
              }
            }

            const totalTableRows =
              field.type === "table"
                ? formQuestion.tableRows ??
                  formQuestion.tableQuestions?.length ??
                  field.tableRows ??
                  0
                : 0;
            // Yeni format için: Her satırda seçili hücreleri say (cellKey formatında: "rowIdx_headerIdx")
            const tableAnswerCount =
              field.type === "table"
                ? new Set(
                    Object.keys(tableAnswers)
                      .filter(key => {
                        // Yeni format: "0_0", "1_2" gibi -> sadece true olanları veya değeri olanları say
                        const value = tableAnswers[key];
                        return value === true || (typeof value === "string" && value !== "");
                      })
                      .map(cellKey => cellKey.split('_')[0]) // Satır index'ini al
                  ).size
                : 0;

            let imageAnswers = [];
            const imageMode = formQuestion.imageAnswerMode || field.imageAnswerMode || "select";
            let drawingAnswer = "";
            if (field.type === "image") {
              try {
                const parsed =
                  typeof field.answer === "string"
                    ? JSON.parse(field.answer)
                    : field.answer;
                if (Array.isArray(parsed)) {
                  imageAnswers = parsed;
                } else if (typeof field.answer === "string" && field.answer.startsWith("data:image/")) {
                  drawingAnswer = field.answer;
                } else {
                  imageAnswers = [];
                }
              } catch (error) {
                if (typeof field.answer === "string" && field.answer.startsWith("data:image/")) {
                  drawingAnswer = field.answer;
                } else {
                  imageAnswers = [];
                }
              }
            }
            const imageAnswerCount =
              field.type === "image" ? imageAnswers.length : 0;

            const selectedOption =
              field.type === "multiple_choice"
                ? (formQuestion.options || []).find(
                    (opt) => opt.option === field.answer
                  )
                : null;

            // Backend scoringEngine: soru yalnızca hem cevap hem form şablonu hasImportance !== false ise puanlanır
            const answerCountsForScoring = field.hasImportance !== false;
            const templateCountsForScoring = formQuestion.hasImportance !== false;
            const includedInScoreCalculation =
              answerCountsForScoring && templateCountsForScoring;

            const isAnswered =
              field.type === "text"
                ? Boolean(textAnswer && textAnswer.trim() !== "")
                : field.type === "multiple_choice"
                ? Boolean(selectedOption)
                : field.type === "table"
                ? totalTableRows > 0 && tableAnswerCount === totalTableRows
                : field.type === "image"
                ? (imageMode === "draw" ? Boolean(drawingAnswer) : imageAnswerCount > 0)
                : field.type === "rating_scale"
                ? (() => {
                    const raw = field.value !== undefined && field.value !== null
                      ? field.value
                      : field.answer;
                    const n = Number(raw);
                    return !Number.isNaN(n) && n >= 0 && n <= 10;
                  })()
                : Boolean(field.answer);
            return (
              <Box key={index} sx={{ mb: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        background: meta.gradient,
                        color: colors.grey[100],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 6px 16px ${colors.primary[900]}40`,
                      }}
                    >
                      {meta.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ mb: 0.2, color: colors.grey[100], fontWeight: 700 }}
                      >
                        {t('questionNumberWithIndex', { index: index + 1 })}
                      </Typography>
                      {meta.subtitle && (
                        <Typography
                          variant="body2"
                          sx={{ color: colors.grey[300], opacity: 0.85 }}
                        >
                          {meta.title}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
                    <Chip
                      label={meta.chipLabel}
                      sx={{
                        backgroundColor: `${colors.primary[500]}aa`,
                        color: colors.grey[100],
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 1.5,
                      }}
                    />
                    <Chip
                      label={isAnswered ? t('answered') : t('pending')}
                      sx={{
                        backgroundColor: isAnswered
                          ? `${colors.greenAccent[600]}aa`
                          : `${colors.redAccent[600]}aa`,
                        color: colors.grey[100],
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 1.5,
                      }}
                    />
                    <Chip
                      icon={
                        <StarOutlineOutlinedIcon
                          sx={{
                            fontSize: "1rem !important",
                            color: `${colors.grey[100]} !important`,
                          }}
                        />
                      }
                      label={
                        includedInScoreCalculation
                          ? t("questionScoreInclusionIncluded")
                          : t("questionScoreInclusionExcluded")
                      }
                      sx={{
                        backgroundColor: includedInScoreCalculation
                          ? `${colors.blueAccent[600]}aa`
                          : `${colors.grey[700]}aa`,
                        color: colors.grey[100],
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 1.5,
                        maxWidth: { xs: "100%", sm: 280 },
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          textAlign: "left",
                          py: 0.5,
                        },
                      }}
                    />
                    {field.type === "table" && (
                      <Chip
                        label={t('rowsCount', { count: tableAnswerCount, total: totalTableRows })}
                        sx={{
                          backgroundColor: `${colors.primary[500]}aa`,
                          color: colors.grey[100],
                          fontWeight: 600,
                          borderRadius: 2,
                          px: 1.5,
                        }}
                      />
                    )}
                    {field.type === "image" && (
                      <Chip
                        label={t('areasCount', { count: imageAnswerCount })}
                        sx={{
                          backgroundColor: `${colors.primary[500]}aa`,
                          color: colors.grey[100],
                          fontWeight: 600,
                          borderRadius: 2,
                          px: 1.5,
                        }}
                      />
                    )}
                    {field.type === "multiple_choice" &&
                      includedInScoreCalculation &&
                      selectedOption?.option_level !== undefined && (
                      <Chip
                        label={t('scoreLabel', { score: selectedOption.option_level })}
                        sx={{
                          backgroundColor: `${colors.greenAccent[600]}aa`,
                          color: colors.grey[100],
                          fontWeight: 600,
                          borderRadius: 2,
                          px: 1.5,
                        }}
                      />
                    )}
                  </Stack>
                </Box>
                <Box
                  sx={{
                    borderRadius: 5,
                    boxShadow: `0 14px 32px ${colors.primary[900]}35`,
                    border: `1px solid ${colors.primary[200]}30`,
                    borderLeft: `4px solid ${colors.greenAccent[500]}`,
                    backgroundColor: colors.primary[500],
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        color: colors.grey[300],
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      {t('questionText')}
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={field.question}
                      InputProps={{
                        readOnly: true,
                        sx: {
                          borderRadius: 3,
                          backgroundColor: colors.primary[500],
                          fontSize: "1.05rem",
                          fontWeight: 600,
                          color: colors.grey[100],
                          "& fieldset": {
                            borderColor: `${colors.primary[200]}44`,
                          },
                        },
                      }}
                    />
                  </Box>

                  {field.type === "text" && (
                    <Box sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          color: colors.grey[300],
                          letterSpacing: 0.5,
                          textTransform: "uppercase",
                        }}
                      >
                        {t('patientAnswer')}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        value={textAnswer || "—"}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            borderRadius: 3,
                            backgroundColor: colors.primary[500],
                            color: colors.grey[100],
                            "& fieldset": {
                              borderColor: `${colors.primary[200]}33`,
                            },
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          textAlign: "right",
                          mt: 0.5,
                          color: colors.grey[400],
                        }}
                      >
                        {t('charactersCount', { count: textAnswer?.length ?? 0 })}
                      </Typography>
                    </Box>
                  )}

                  {field.type === "rating_scale" && (() => {
                    const raw =
                      field.value !== undefined && field.value !== null
                        ? field.value
                        : field.answer;
                    const n = Math.max(
                      0,
                      Math.min(10, Math.round(Number(raw) || 0))
                    );
                    return (
                      <Box sx={{ px: { xs: 2, md: 3 }, pb: 3, maxWidth: 760 }}>
                        <RatingScaleSlider
                          value={n}
                          onChange={() => {}}
                          minLabel={formQuestion.minLabel || "0"}
                          maxLabel={formQuestion.maxLabel || "10"}
                          positiveEnd={
                            formQuestion.positiveEnd === "max" ? "max" : "min"
                          }
                          disabled
                          colors={colors}
                        />
                        {includedInScoreCalculation &&
                          field.option_level != null && (
                            <Typography
                              variant="caption"
                              sx={{ display: "block", mt: 1, color: colors.grey[300] }}
                            >
                              {t("importanceScore", { score: field.option_level })}
                            </Typography>
                          )}
                      </Box>
                    );
                  })()}

                  {field.type === "multiple_choice" && (
                    <Box sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          color: colors.grey[300],
                          letterSpacing: 0.5,
                          textTransform: "uppercase",
                        }}
                      >
                        {t('options')}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {(formQuestion.options || []).map(
                          (optionObj, optionIdx) => {
                            const isSelected =
                              selectedOption &&
                              optionObj.option === selectedOption.option;
                            return (
                              <Box
                                key={optionIdx}
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: { xs: "auto 1fr" },
                                  gap: 2,
                                  alignItems: "center",
                                  borderRadius: 3,
                                  border: `1px solid ${colors.primary[200]}33`,
                                  boxShadow: `0 8px 18px ${colors.primary[900]}25`,
                                  px: { xs: 1.5, md: 2.5 },
                                  py: { xs: 1.2, md: 1.5 },
                                  backgroundColor: isSelected
                                    ? `${colors.greenAccent[600]}33`
                                    : `${colors.primary[600]}AA`,
                                }}
                              >
                                <Radio checked={isSelected} disabled color="success" />
                                <Box>
                                  <Typography
                                    sx={{
                                      color: colors.grey[100],
                                      fontWeight: isSelected ? 700 : 500,
                                    }}
                                  >
                                    {optionObj.option}
                                  </Typography>
                                  {includedInScoreCalculation &&
                                    optionObj.option_level !== undefined && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: colors.grey[300],
                                        display: "block",
                                      }}
                                    >
                                      {t('importance')}: {optionObj.option_level}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          }
                        )}
                      </Box>
                      {selectedOption && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: colors.primary[600],
                            border: `1px solid ${colors.greenAccent[600]}55`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: colors.greenAccent[400], fontWeight: 600 }}
                          >
                            {t('selectedOption', { option: selectedOption.option })}
                          </Typography>
                          {includedInScoreCalculation &&
                            selectedOption.option_level !== undefined &&
                            selectedOption.option_level !== null && (
                            <Typography
                              variant="caption"
                              sx={{ color: colors.grey[400] }}
                            >
                              {t('importanceScore', { score: selectedOption.option_level })}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                  {field.type === "image" && (
                    <Box sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
                      <Box sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 2,
                        mb: 3
                      }}>
                        <ImageIcon sx={{ 
                          fontSize: 28,
                          color: colors.greenAccent[500]
                        }} />
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            fontWeight: "bold",
                            background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent"
                          }}
                        >
                          {t('visualQuestionICRS')}
                        </Typography>
                        <Box sx={{ 
                          ml: "auto",
                          display: "flex",
                          gap: 1,
                          alignItems: "center"
                        }}>
                          <Box sx={{ 
                            px: 2, 
                            py: 0.5, 
                            borderRadius: 2,
                            backgroundColor: colors.blueAccent[700],
                            border: `1px solid ${colors.blueAccent[500]}`
                          }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                              {imageMode === "draw" ? "Mode: Draw" : t('totalAreas', { count: formQuestion.areas?.length || 0 })}
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            px: 2, 
                            py: 0.5, 
                            borderRadius: 2,
                            backgroundColor: colors.greenAccent[700],
                            border: `1px solid ${colors.greenAccent[500]}`
                          }}>
                          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                              {imageMode === "draw"
                                ? drawingAnswer
                                  ? "Drawing available"
                                  : "No drawing"
                                : t('selectedCount', { count: imageAnswerCount })}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {imageMode === "draw" ? (
                        <Box
                          sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            border: `1px solid ${colors.primary[200]}44`,
                            backgroundColor: colors.primary[700],
                            p: 1,
                            position: "relative",
                          }}
                        >
                          {(formQuestion.imageUrl || field.imageUrl) && (
                            <img
                              src={formQuestion.imageUrl || field.imageUrl}
                              alt="question base"
                              style={{ width: "100%", maxHeight: 500, objectFit: "contain", display: "block" }}
                            />
                          )}
                          {drawingAnswer ? (
                            <img
                              src={drawingAnswer}
                              alt="drawn answer"
                              style={{
                                width: "100%",
                                maxHeight: 500,
                                objectFit: "contain",
                                display: "block",
                                position: (formQuestion.imageUrl || field.imageUrl) ? "absolute" : "relative",
                                inset: (formQuestion.imageUrl || field.imageUrl) ? 8 : "auto",
                                pointerEvents: "none",
                              }}
                            />
                          ) : !(formQuestion.imageUrl || field.imageUrl) ? (
                            <Typography sx={{ color: colors.grey[300], p: 2 }}>No image or drawing answer.</Typography>
                          ) : null}
                        </Box>
                      ) : (
                        (() => {
                        const selectedAreas = imageAnswers;
                        const displayAreas = showOnlySelected[index]
                          ? (formQuestion.areas || []).filter((area) =>
                              selectedAreas.some((sa) => sa.id === area.id)
                            )
                          : formQuestion.areas || [];

                        return (
                          <>
                            <CartilageMap
                              imageUrl={formQuestion.imageUrl || field.imageUrl}
                              areas={displayAreas}
                              selectedAreas={selectedAreas}
                              multiSelect={true}
                              onAreaSelect={() => {}}
                              disabled={true}
                              showLabels={true}
                              showOnlySelectedButton={true}
                              showOnlySelected={showOnlySelected[index] || false}
                              onToggleShowOnlySelected={() => {
                                setShowOnlySelected((prev) => ({
                                  ...prev,
                                  [index]: !prev[index],
                                }));
                              }}
                            />

                            {selectedAreas.length > 0 && (
                              <Box
                                sx={{
                                  mt: 3,
                                  p: 2,
                                  borderRadius: 2,
                                  backgroundColor: colors.primary[600],
                                  border: `2px solid ${colors.greenAccent[700]}`,
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{ mb: 2, color: colors.greenAccent[400] }}
                                >
                                  {t('patientsSelectedAreas', { count: selectedAreas.length })}
                                </Typography>
                                <Box
                                  sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                                >
                                  {selectedAreas.map((area, idx) => (
                                    <Chip
                                      key={area.id || idx}
                                      label={area.name || `Area ${idx + 1}`}
                                      sx={{
                                        backgroundColor:
                                          area.color || colors.greenAccent[700],
                                        color: colors.grey[100],
                                        fontWeight: "bold",
                                      }}
                                    />
                                  ))}
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1, color: colors.blueAccent[300] }}
                                  >
                                    {t('areaDetails')}
                                  </Typography>
                                  {selectedAreas.map((area, idx) => (
                                    <Box
                                      key={area.id || idx}
                                      sx={{
                                        p: 1.5,
                                        mb: 1,
                                        borderRadius: 1,
                                        backgroundColor: colors.primary[700],
                                        border: `1px solid ${
                                          area.color || colors.greenAccent[700]
                                        }`,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 16,
                                            height: 16,
                                            backgroundColor:
                                              area.color || colors.greenAccent[500],
                                            borderRadius: 1,
                                            border: `2px solid ${colors.grey[700]}`,
                                          }}
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: "bold" }}
                                        >
                                          {area.name}
                                        </Typography>
                                        {area.value && area.value !== area.name && (
                                          <Typography
                                            variant="caption"
                                            sx={{ color: colors.grey[400] }}
                                          >
                                            ({t('valueLabel', { value: area.value })})
                                          </Typography>
                                        )}
                                      </Box>
                                      {area.description && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: colors.grey[400],
                                            mt: 0.5,
                                            display: "block",
                                            ml: 3,
                                          }}
                                        >
                                          {area.description}
                                        </Typography>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </>
                        );
                      })()
                      )}

                      <Box sx={{ 
                        mt: 2,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: colors.primary[600],
                        border: `1px solid ${colors.blueAccent[700]}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                      }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: colors.blueAccent[400],
                            fontStyle: "italic"
                          }}
                        >
                          {t('thisShowsAreasSelectedByPatient')}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {field.type === "table" && (
                    <Box sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
                      <Box sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 2,
                        mb: 3
                      }}>
                        <TableChartIcon sx={{ 
                          fontSize: 28,
                          color: colors.greenAccent[500]
                        }} />
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            fontWeight: "bold",
                            background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent"
                          }}
                        >
                          {t('tableQuestion')}
                        </Typography>
                        <Box sx={{ 
                          ml: "auto",
                          display: "flex",
                          gap: 1,
                          alignItems: "center"
                        }}>
                          <Box sx={{ 
                            px: 2, 
                            py: 0.5, 
                            borderRadius: 2,
                            backgroundColor: colors.blueAccent[700],
                            border: `1px solid ${colors.blueAccent[500]}`
                          }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                              {t('rowsCountLabel', { count: formQuestion.tableRows })}
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            px: 2, 
                            py: 0.5, 
                            borderRadius: 2,
                            backgroundColor: colors.greenAccent[700],
                            border: `1px solid ${colors.greenAccent[500]}`
                          }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                              {t('columnsCountLabel', { count: formQuestion.tableCols })}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ 
                        overflowX: "auto",
                        borderRadius: 2,
                        border: `3px solid ${colors.greenAccent[700]}`,
                        boxShadow: `0 4px 16px rgba(0, 0, 0, 0.2)`,
                        background: colors.primary[500]
                      }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ 
                                background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[800]} 100%)`,
                                fontWeight: "bold",
                                border: `2px solid ${colors.blueAccent[600]}`,
                                color: colors.grey[100],
                                fontSize: "1rem",
                                textAlign: "center",
                                minWidth: 180
                              }}>
                                ❓ {t('questions')}
                              </TableCell>
                              {(formQuestion.tableHeaders || []).map((header, headerIdx) => (
                                <TableCell 
                                  key={headerIdx}
                                  sx={{ 
                                    background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[800]} 100%)`,
                                    fontWeight: "bold",
                                    border: `2px solid ${colors.greenAccent[600]}`,
                                    minWidth: 140,
                                    padding: 2
                                  }}
                                >
                                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
                                    <Typography 
                                      sx={{ 
                                        fontSize: "0.95rem",
                                        fontWeight: "bold",
                                        color: colors.grey[100]
                                      }}
                                    >
                                      {header}
                                    </Typography>
                                    <Box sx={{ 
                                      px: 2, 
                                      py: 0.5, 
                                      borderRadius: 2,
                                      backgroundColor: colors.primary[600],
                                      border: `1px solid ${colors.greenAccent[500]}`
                                    }}>
                                      <Typography 
                                        variant="caption" 
                                        sx={{ 
                                          fontWeight: "bold",
                                          color: colors.grey[100]
                                        }}
                                      >
                                        💯 {t('score')}: {formQuestion.tableHeaderImportance?.[headerIdx] ?? 0}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(formQuestion.tableQuestions || []).map(
                              (question, questionIdx) => (
                                <TableRow
                                  key={questionIdx}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: colors.primary[600],
                                    },
                                    transition: "background-color 0.2s",
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      backgroundColor: colors.blueAccent[900],
                                      border: `1px solid ${colors.blueAccent[700]}`,
                                      minWidth: 180,
                                      color: colors.grey[100],
                                      fontWeight: "500",
                                    }}
                                  >
                                    {question}
                                  </TableCell>
                                  {(formQuestion.tableHeaders || []).map(
                                    (header, headerIdx) => {
                                      // Yeni format: cellKey = "questionIdx_headerIdx" formatında
                                      // Örnek: {"0_0": "Header1", "1_2": "Header3"}
                                      const cellKey = `${questionIdx}_${headerIdx}`;
                                      
                                      // Yeni formatta: tableAnswers[cellKey] === true veya header değeri var ise seçili
                                      // Eski formatta: tableAnswers[questionIdx] === header ise seçili
                                      let isSelected = false;
                                      
                                      if (tableAnswers && typeof tableAnswers === "object") {
                                        // Yeni format kontrolü (cellKey bazlı)
                                        if (tableAnswers[cellKey] === true || tableAnswers[cellKey] === header) {
                                          isSelected = true;
                                        }
                                        // Eski format kontrolü (geriye dönük uyumluluk için)
                                        else if (tableAnswers[questionIdx] === header) {
                                          isSelected = true;
                                        }
                                      }
                                      
                                      return (
                                        <TableCell
                                          key={headerIdx}
                                          sx={{
                                            border: `2px solid ${isSelected ? colors.greenAccent[500] : colors.greenAccent[700]}40`,
                                            textAlign: "center",
                                            background: isSelected
                                              ? `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`
                                              : `linear-gradient(135deg, ${colors.primary[650]} 0%, ${colors.primary[600]} 100%)`,
                                            py: 2.5,
                                            transition: "all 0.2s ease",
                                          }}
                                        >
                                          <Radio
                                            checked={isSelected}
                                            disabled
                                            size="small"
                                            sx={{
                                              color: colors.greenAccent[600],
                                              "&.Mui-checked": {
                                                color: colors.greenAccent[300],
                                              },
                                              "&.Mui-disabled": {
                                                color: isSelected
                                                  ? colors.greenAccent[300]
                                                  : colors.greenAccent[700],
                                              },
                                            }}
                                          />
                                        </TableCell>
                                      );
                                    }
                                  )}
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                      <Box sx={{ 
                        mt: 2,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: colors.primary[600],
                        border: `1px solid ${colors.blueAccent[700]}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                      }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: colors.blueAccent[400],
                            fontStyle: "italic"
                          }}
                        >
                          {t('thisTableShowsSubmittedAnswers')}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
